import { createContext, useContext, useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '@/lib/firebase';

interface LockerSettings {
  feeCollectorAddress: string;
  lockFee: number;
  extendLockFee: number;
  withdrawFee: number;
}

interface LockerSettingsContextType {
  settings: LockerSettings;
  isLoading: boolean;
}

const DEFAULT_LOCKER_SETTINGS: LockerSettings = {
  feeCollectorAddress: "0x760A0D5cC0ca242eB83609C627AB00Ac3F726Ba7",
  lockFee: 0.1, // 0.1 BNB
  extendLockFee: 0.05, // 0.05 BNB
  withdrawFee: 0.1, // 0.1 BNB
};

const LockerSettingsContext = createContext<LockerSettingsContextType | undefined>(undefined);

export function LockerSettingsProvider({ children }: { children: React.ReactNode }) {
  const [settings, setSettings] = useState<LockerSettings>(DEFAULT_LOCKER_SETTINGS);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      doc(db, "settings", "fees"),
      (doc) => {
        if (doc.exists()) {
          const data = doc.data();
          // Get the BSC network fees (chainId 56)
          const bscFees = data.networkFees?.[56] || {};
          const collectors = data.collectors || [];
          
          setSettings({
            feeCollectorAddress: collectors[0]?.address || DEFAULT_LOCKER_SETTINGS.feeCollectorAddress,
            lockFee: parseFloat(bscFees.lockFee) || DEFAULT_LOCKER_SETTINGS.lockFee,
            extendLockFee: parseFloat(bscFees.lockFee) / 2 || DEFAULT_LOCKER_SETTINGS.extendLockFee,
            withdrawFee: parseFloat(bscFees.lockFee) || DEFAULT_LOCKER_SETTINGS.withdrawFee,
          });
        }
        setIsLoading(false);
      },
      (error) => {
        console.error('Error loading locker settings:', error);
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  return (
    <LockerSettingsContext.Provider value={{ settings, isLoading }}>
      {children}
    </LockerSettingsContext.Provider>
  );
}

export function useLockerSettings() {
  const context = useContext(LockerSettingsContext);
  if (context === undefined) {
    throw new Error('useLockerSettings must be used within a LockerSettingsProvider');
  }
  return context;
}
